import { parse, parseISO, format, isValid } from 'date-fns';
import enUsLocale from 'date-fns/locale/en-US';
import enGbLocale from 'date-fns/locale/en-GB';
import isEmpty from 'lodash/isEmpty';
import { personalId as personalIdValidator } from '@klarna/mf-validations';
import { PERSONAL_ID_DOCUMENT_TYPE } from '@/constants';

export const DATE_LOCALES = {
  US: enUsLocale,
  EU: enGbLocale,
};

export const formatDate = (country, date) => {
  const parsedDate = parseISO(date);

  if (!isValid(parsedDate)) {
    return null;
  }

  return format(parsedDate, 'P', {
    locale: country.toUpperCase() === 'US' ? DATE_LOCALES.US : DATE_LOCALES.EU,
  });
};

export const createDateFormatter = country => date => formatDate(country, date);

// derive the DoB from the personal ID

// SE
export const getSwedishDOB = personalId => {
  if (
    personalIdValidator.validate(personalId, {
      country: 'SE',
      documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
    }).error
  ) {
    return null;
  }

  const year = personalId.slice(0, 4);
  const month = personalId.slice(4, 6);
  let day = Number(personalId.slice(6, 8));

  if (day > 60) day = day - 60;
  const formattedDay = day < 10 ? `0${day}` : day;

  return `${year}-${month}-${formattedDay}`;
};

// FI
export const getFinnishDOB = personalId => {
  if (
    personalIdValidator.validate(personalId, {
      country: 'FI',
      documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_CODE,
    }).error
  ) {
    return null;
  }

  const day = personalId.slice(0, 2);
  const month = personalId.slice(2, 4);
  const year = personalId.slice(4, 6);
  const yearSymbol = personalId.slice(6, 7);

  let yearPrefix = '';
  switch (yearSymbol) {
    case '+':
      yearPrefix = '18';
      break;
    case '-':
      yearPrefix = '19';
      break;
    case 'A':
      yearPrefix = '20';
      break;
    default:
      yearPrefix = '19';
  }

  return `${yearPrefix}${year}-${month}-${day}`;
};

// NO
export const getNorwegianDOB = personalId => {
  if (
    personalIdValidator.validate(personalId, {
      country: 'NO',
      documentType: PERSONAL_ID_DOCUMENT_TYPE.BIRTH_NUMBER,
    }).error
  ) {
    return null;
  }

  const day = personalId.slice(0, 2);
  const month = personalId.slice(2, 4);
  const year = personalId.slice(4, 6);
  const yearCode = Number(personalId.slice(6, 9));

  let yearPrefix = '';
  const yearAsNumber = Number(year);
  if (yearCode >= 500 && yearCode <= 749 && yearAsNumber >= 54 && yearAsNumber <= 99) {
    yearPrefix = '18';
  } else if (
    (yearCode >= 0 && yearCode <= 499) ||
    (yearCode >= 900 && yearCode <= 999 && yearAsNumber >= 40 && yearAsNumber <= 99)
  ) {
    yearPrefix = '19';
  } else if (yearCode >= 500 && yearCode <= 999 && yearAsNumber >= 0 && yearAsNumber <= 39) {
    yearPrefix = '20';
  }

  // pass year as original string because of '0x' values
  return `${yearPrefix}${year}-${month}-${day}`;
};

// DK
export const getDanishDOB = personalId => {
  if (
    personalIdValidator.validate(personalId, {
      country: 'DK',
      documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
    }).error
  ) {
    return null;
  }

  const day = personalId.slice(0, 2);
  const month = personalId.slice(2, 4);
  const year = personalId.slice(4, 6);
  const yearCode = Number(personalId.slice(7, 8));

  let yearPrefix = '';
  const yearAsNumber = Number(year);
  if (yearCode >= 0 && yearCode <= 3) {
    yearPrefix = '19';
  } else if (yearCode === 4 || yearCode === 9) {
    if (yearAsNumber >= 0 && yearAsNumber <= 36) {
      yearPrefix = '20';
    } else if (yearAsNumber >= 37) {
      yearPrefix = '19';
    }
  } else if (yearCode >= 5 && yearCode <= 8) {
    if (yearAsNumber >= 0 && yearAsNumber <= 57) {
      yearPrefix = '20';
    } else if (yearAsNumber >= 58) {
      yearPrefix = '18';
    }
  }

  // pass year as original string because of '0x' values
  return `${yearPrefix}${year}-${month}-${day}`;
};

// Polish date of birth from PESEL
export const getPolishDOB = pesel => {
  if (
    personalIdValidator.validate(pesel, {
      country: 'PL',
      documentType: PERSONAL_ID_DOCUMENT_TYPE.PERSONAL_IDENTIFICATION_NUMBER,
    }).error
  ) {
    return null;
  }

  let year = Number(pesel.substr(0, 2));
  let month = Number(pesel.substr(2, 2));
  const day = Number(pesel.substr(4, 2));

  if (month < 20) {
    year = 1900 + year;
  } else if (month < 40) {
    year = 2000 + year;
    month -= 20;
  } else if (month < 60) {
    year = 2100 + year;
    month -= 40;
  } else if (month < 80) {
    year = 2200 + year;
    month -= 60;
  } else {
    year = 1800 + year;
    month -= 80;
  }

  const extractedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  if (!isValid(parse(extractedDate, 'yyyy-MM-dd', new Date()))) {
    return null;
  }

  return extractedDate;
};

export const getDateOfBirthFromPersonalId = (country, personalId) => {
  if (isEmpty(personalId)) {
    return null;
  }

  switch (country) {
    case 'SE':
      return getSwedishDOB(personalId);
    case 'FI':
      return getFinnishDOB(personalId);
    case 'NO':
      return getNorwegianDOB(personalId);
    case 'DK':
      return getDanishDOB(personalId);
    case 'PL':
      return getPolishDOB(personalId);
    // default value for the bubble-ui date field must be null
    default:
      return null;
  }
};
